import React, { useState } from 'react';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Butterfly1000 = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const butterflyEmojis = [
    '🦋', '🌸', '✨', '💫', '🌺', '🌼', '🌻', '🌹', '🌷', '🌱'
  ];

  const butterflies = Array(1000)
    .fill(null)
    .map(() => butterflyEmojis[Math.floor(Math.random() * butterflyEmojis.length)])
    .join('');

  const handleCopy = () => {
    navigator.clipboard.writeText(butterflies);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>1000 Butterfly Emojis 🦋 Copy and Paste [{currentYear}] ✨</title>
        <meta name="description" content={`【INSTANT COPY】 Get 1000 butterfly emojis! 🦋 Perfect mix of butterflies and flowers ✨ One-click copy & paste 🦋 Works everywhere! Updated for ${currentYear} 🌸 Free butterfly emojis for Instagram, WhatsApp & more!`} />
        <link rel="canonical" href="https://emojicollection.net/butterfly-1000" />
        
        <meta property="og:title" content={`1000 Butterfly Emojis 🦋 Copy and Paste [${currentYear}] ✨`} />
        <meta property="og:description" content={`Get 1000 butterfly emojis! 🦋 Perfect mix of butterflies and flowers ✨ One-click copy & paste 🦋 Works everywhere! Updated for ${currentYear}`} />
        <meta property="og:url" content="https://emojicollection.net/butterfly-1000" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`1000 Butterfly Emojis 🦋 Copy and Paste [${currentYear}] ✨`} />
        <meta name="twitter:description" content={`Get 1000 butterfly emojis! 🦋 Perfect mix of butterflies and flowers ✨ One-click copy & paste 🦋 Works everywhere! Updated for ${currentYear}`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="1000 butterfly emojis, butterfly emoji copy paste, butterfly emojis, nature emojis, garden emojis, instagram butterflies, whatsapp butterflies" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=1200&q=80"
                alt="Beautiful butterflies and flowers"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-4">
                <span className="text-2xl">🦋</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                1000 Butterfly Emojis 🦋 Copy and Paste [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get instant access to 1000 beautiful butterfly emojis! Perfect mix of butterflies, flowers, and sparkles. Updated for {currentYear} with the latest designs. One-click copy and paste feature!
              </p>
            </header>

            <section className="max-w-2xl mx-auto mb-12">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  🦋 Copy 1000 Butterflies Instantly
                </h2>
                <div className="mb-4 overflow-auto max-h-96 text-left break-words text-2xl">
                  {butterflies}
                </div>
                <button
                  onClick={handleCopy}
                  className={`inline-flex items-center px-6 py-3 rounded-lg text-lg font-medium transition-all transform hover:scale-105 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={20} />
                      Copied 1000 Butterflies!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={20} />
                      Copy 1000 Butterflies
                    </>
                  )}
                </button>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold mb-4">✨ Butterfly & Nature Emojis</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Emoji Meanings</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">🦋 Butterfly - Beauty & transformation</li>
                    <li className="flex items-center">🌸 Cherry Blossom - Spring & renewal</li>
                    <li className="flex items-center">✨ Sparkles - Magic & wonder</li>
                    <li className="flex items-center">💫 Dizzy - Enchantment</li>
                    <li className="flex items-center">🌺 Hibiscus - Delicate beauty</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-4">Features</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">✨ 1000 beautiful emojis</li>
                    <li className="flex items-center">🌈 Perfect emoji mix</li>
                    <li className="flex items-center">📱 Works on all devices</li>
                    <li className="flex items-center">🔄 Random selection</li>
                    <li className="flex items-center">📋 One-click copy feature</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">📱 Perfect For</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Nature Content</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Garden photos</li>
                    <li>Spring themes</li>
                    <li>Nature blogs</li>
                    <li>Outdoor posts</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Social Media</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Instagram posts</li>
                    <li>Facebook updates</li>
                    <li>Twitter messages</li>
                    <li>TikTok captions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Messaging</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>WhatsApp</li>
                    <li>Messenger</li>
                    <li>iMessage</li>
                    <li>SMS</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Why use butterfly emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Butterfly emojis symbolize beauty, transformation, and nature's grace. They're perfect for adding a delicate and enchanting touch to your messages and social media posts.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Do these emojis work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! These butterfly emojis are universal and work on all major platforms including Instagram, Facebook, WhatsApp, and more. They're compatible with both mobile and desktop devices.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Are these emojis free to use?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Absolutely! Our butterfly emojis are completely free to use for both personal and commercial purposes. No attribution required.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default Butterfly1000;